import { useEffect, useRef, useState, useId } from "react";

import { useTranslations } from "next-intl";

import type { ComponentProps } from "react";

interface FitIframeProps extends ComponentProps<"iframe"> {
  baseHref?: string;
  content?: string;
}

const CSP = `
  default-src 'none';
  script-src 'unsafe-inline';
  style-src 'unsafe-inline';
  font-src http: https:;
  base-uri 'none';
  form-action 'none';
  img-src data: 'self';
  media-src data: 'self';
`
  .replace(/\s+/g, " ")
  .trim();

export function FitIframe(props: FitIframeProps) {
  const { content, baseHref, ...rest } = props;
  const t = useTranslations("codeBlock");
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const id = useId();

  useEffect(() => {
    if (!ref.current) return;

    // limited support; see https://developer.mozilla.org/en-US/docs/Web/API/HTMLIFrameElement/csp
    ref.current.setAttribute("csp", CSP);

    const handleMessage = (
      event: MessageEvent<{ type?: "resize"; height?: number; id?: string }>,
    ) => {
      if (event.data?.type === "resize" && id === event.data.id && event.data.height) {
        setHeight(Math.ceil(event.data.height));
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [content, id]);

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      ${baseHref ? `<base href="${baseHref}">` : ""}
      <meta charset="utf-8">
      <meta http-equiv="Content-Security-Policy" content="${CSP}">
      <style>
        @font-face {
          font-family: "GeistMono";
          src: url("/fonts/GeistMono-Variable.woff2") format("woff2-variations");
          font-weight: 100 900;
        }
        html, body {
          margin: 0;
          font-family: GeistMono, ui-monospace, SFMono-Regular, "Roboto Mono", Menlo, Monaco, "Liberation Mono", "DejaVu Sans Mono", "Courier New", monospace;
          font-size: 13px;
          line-height: 1.4;
          letter-spacing: -0.03rem;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        * {
          font-family: inherit;
        }
      </style>
      <script>
        const resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            if (entry.contentBoxSize) {
              const contentBoxSize = entry.contentBoxSize[0];
              const height = contentBoxSize.blockSize || contentBoxSize.inlineSize;
              window.top.postMessage({ type: 'resize', height, id: "${id}" }, '*');
            }
          }
        });

        window.addEventListener("load", () => {
          const height = document.body.scrollHeight;
          window.top.postMessage({ type: 'resize', height, id: "${id}" }, '*');
          resizeObserver.observe(document.documentElement);
        });
      </script>
    </head>
    <body>
      ${content}
    </body>
    </html>
  `;

  return (
    <iframe
      ref={ref}
      title={t("outputFrameTitle")}
      srcDoc={html}
      sandbox="allow-scripts"
      {...rest}
      style={{ height }}
    />
  );
}
